import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { tv } from "tailwind-variants";
import { Button, Markdown } from "../../../..";
import { cn } from "../../../../util/classNameConstructor";
import { Input, type TextareaProps } from "../../../core/inputs/Input";

const EditableContentStyles = tv({
  slots: {
    text: "select-text hyphens-auto whitespace-pre-wrap [word-break:break-word] [overflow-wrap:anywhere] py-2",
    input: "hyphens-auto whitespace-pre-wrap",
  },
});

export type EditableContentProps = Omit<TextareaProps, "type"> & {
  defaultValue?: string;
  pageAlignment?: "left" | "center" | "right";
  mode?: "edit" | "display";
  as?: React.ElementType;
};

export default function EditableContent({
  silent = true,
  className = "body-md",
  rows = 1,
  classNames,
  defaultValue,
  onChange,
  pageAlignment = "left",
  mode = "edit",
  as = "div",
  ...props
}: EditableContentProps) {
  const { t } = useTranslation();
  const [showResetButton, setShowResetButton] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>(null);

  const handleOnChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setShowResetButton(false);
    onChange?.(e);
  };

  useEffect(() => {
    const handleMouseMove = () => {
      setShowResetButton(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowResetButton(false);
      }, 1000);
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const { text, input } = EditableContentStyles();

  if (mode === "display") {
    if (props.value == null && defaultValue == null) return null;
    if (props.rich) {
      return (
        <Markdown
          className={cn(
            text(),
            pageAlignment === "center" && "text-center",
            className,
          )}
        >
          {props.value ?? defaultValue}
        </Markdown>
      );
    }
    const Component = as;
    return (
      <Component className={cn(text(), className)}>
        {props.value ?? defaultValue}
      </Component>
    );
  }
  return (
    <Input
      {...props}
      value={props.value ?? defaultValue}
      onChange={handleOnChange}
      silent={silent}
      className={cn(
        input(),
        pageAlignment === "center" && "text-center",
        className,
      )}
      rows={rows}
      classNames={{
        ...classNames,
        input: classNames?.input,
        container: cn(
          classNames?.container,
          pageAlignment === "center" && "mx-auto",
        ),
      }}
      append={
        defaultValue != null &&
        defaultValue !== props.value &&
        props.value != null && (
          <Button
            size="sm"
            className={cn(
              "opacity-0 hover:opacity-100",
              showResetButton &&
                "group-hover/input-container:opacity-100 group-has-[:focus-visible]/input-container:opacity-100",
            )}
            aria-hidden={!showResetButton}
            tabIndex={showResetButton ? 0 : -1}
            classNameWrapper={cn("self-baseline -ms-8 -my-0.5")}
            leadingIcon="reload"
            tooltipLabel={t("global:reset")}
            onClick={() => {
              const syntheticEvent = {
                target: {
                  value: defaultValue,
                },
              } as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
              onChange?.(syntheticEvent);
            }}
          />
        )
      }
      type={
        props.rich || rows > 1 || Number(props.maxRows) > 1
          ? "textarea"
          : undefined
      }
    />
  );
}
